import { Building2, CheckCircle2, ClipboardList, Clock, type LucideIcon } from 'lucide-react'

import type { BoardShortcuts } from '../../../hooks/useBoardShortcuts'

/**
 * The headline KPI strip atop the Dashboard tab — Total Sites · Total Issues · Completed · Pending.
 * The values are supplied by the parent so they recompute live under the dashboard's age filter
 * (`filtered`) and fall back to the district totals when nothing is selected. Native to the V6
 * language; every card is a shortcut into the board that answers it. Issue-derived numbers show "—"
 * during the feed's cold load — never a fake 0.
 */

interface Accent {
  bg: string
  fg: string
}

// Soft icon-circle tints, aligned to the dashboard's chip palette (the green/amber match DistrictPulse).
const BLUE: Accent = { bg: '#eef2fb', fg: '#3b5bd0' }
const PURPLE: Accent = { bg: '#f3effc', fg: '#7c4dcf' }
const GREEN: Accent = { bg: '#f0fdf4', fg: '#15803d' }
const AMBER: Accent = { bg: '#fffbeb', fg: '#b45309' }

function HeadlineCard({ icon: Icon, label, value, sub, accent, onClick }: {
  icon: LucideIcon
  label: string
  value: string
  sub: string
  accent: Accent
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex min-w-0 items-center gap-3 rounded-[12px] border border-gis-line bg-white px-3 py-[11px] text-left transition-colors hover:border-[#c7d2ea] hover:bg-[#fbfcfe] focus-visible:ring-2 focus-visible:ring-blue-500/60"
    >
      <span
        className="grid h-10 w-10 flex-none place-items-center rounded-full"
        style={{ background: accent.bg }}
      >
        <Icon className="h-[19px] w-[19px]" style={{ color: accent.fg }} aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[10px] font-bold tracking-wider text-gis-muted uppercase">
          {label}
        </span>
        <span className="block text-[22px] leading-none font-bold tracking-[-.01em] text-gis-ink tabular-nums @[620px]/dash:text-[24px]">
          {value}
        </span>
        <span className="mt-[3px] block truncate text-[10.5px] text-gis-soft">{sub}</span>
      </span>
    </button>
  )
}

interface DashboardKpiRowProps {
  totalSites: number
  totalIssues: number
  completed: number
  pending: number
  /** Issue feed cold-load — issue-derived tiles show "—". */
  loading: boolean
  /** A dashboard filter is active — swap the context sub-labels so the changed numbers read right. */
  filtered: boolean
  go: BoardShortcuts
}

export function DashboardKpiRow({ totalSites, totalIssues, completed, pending, loading, filtered, go }: DashboardKpiRowProps) {
  const issueNum = (n: number): string => (loading ? '—' : n.toLocaleString())

  return (
    <div className="grid grid-cols-2 gap-3 @[720px]/dash:grid-cols-4">
      <HeadlineCard
        icon={Building2}
        accent={BLUE}
        label="Total Sites"
        value={totalSites.toLocaleString()}
        sub={filtered ? 'with matching issues' : 'All active sites'}
        onClick={() => go.goCoverage()}
      />
      <HeadlineCard
        icon={ClipboardList}
        accent={PURPLE}
        label="Total Issues"
        value={issueNum(totalIssues)}
        sub={filtered ? 'in current filter' : 'Raised in this period'}
        onClick={go.goIssuesAll}
      />
      <HeadlineCard
        icon={CheckCircle2}
        accent={GREEN}
        label="Completed"
        value={issueNum(completed)}
        sub={filtered ? 'in current filter' : 'Completed in this period'}
        onClick={go.goIssuesClosed}
      />
      <HeadlineCard
        icon={Clock}
        accent={AMBER}
        label="Pending"
        value={issueNum(pending)}
        sub={filtered ? 'in current filter' : 'Still open'}
        onClick={() => go.goIssues()}
      />
    </div>
  )
}
